import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import {
  FormBuilder,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  Subject,
  debounceTime,
  distinctUntilChanged,
  filter,
  takeUntil,
} from 'rxjs';
import { AxiosError } from 'axios';
import {
  AttorneyItem,
  AttorneyService,
} from 'src/app/services/attorney/attorney.service';
import { Address } from 'src/app/services/cases/cases.service';
import {
  CountriesService,
  Country,
} from 'src/app/services/countries/countries.service';
import { HelpersService } from 'src/app/services/helpers/helpers.service';
import {
  LawFirmItem,
  LawFirmService,
} from 'src/app/services/law-firm/law-firm.service';
import { PayeePopupComponent } from '../../../../shared/payee-popup/payee-popup.component';
import { PayeeFormFieldComponent } from '../../../../shared/payee-form-fields/payee-form-field/payee-form-field.component';
import { AddressFormComponent } from '../../../../helper/edit-case-dialog/address-form/address-form.component';
import { PayeePhoneComponent } from '../../../../shared/payee-form-fields/payee-phone/payee-phone.component';
import { PayeeAutocompleteComponent } from '../../../../shared/payee-form-fields/payee-autocomplete/payee-autocomplete.component';
import { PayeeMiniIconButtonComponent } from '../../../../shared/payee-buttons/payee-mini-icon-button/payee-mini-icon-button.component';
import { PayeeIconComponent } from '../../../../shared/payee-icon/payee-icon.component';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-update-law-firm-dialog',
  templateUrl: './update-law-firm-dialog.component.html',
  styleUrls: ['./update-law-firm-dialog.component.scss'],
  imports: [
    PayeePopupComponent,
    PayeeFormFieldComponent,
    FormsModule,
    ReactiveFormsModule,
    AddressFormComponent,
    PayeePhoneComponent,
    PayeeAutocompleteComponent,
    PayeeMiniIconButtonComponent,
    PayeeIconComponent,
    TranslateModule,
  ],
})
export class UpdateLawFirmDialogComponent implements OnInit, OnDestroy {
  private dialogRef =
    inject<MatDialogRef<UpdateLawFirmDialogComponent, boolean>>(MatDialogRef);
  private lawFirmService = inject(LawFirmService);
  private attorneyService = inject(AttorneyService);
  private snackbar = inject(MatSnackBar);
  private fb = inject(FormBuilder);
  private helpersService = inject(HelpersService);
  private countriesService = inject(CountriesService);
  private readonly destroy = new Subject<void>();

  data = inject<LawFirmItem>(MAT_DIALOG_DATA);

  readonly searchAttorneys$ = new Subject<string>();
  readonly selectedAttorney = this.fb.control<AttorneyItem | null>(null);
  attorneyOptions: AttorneyItem[] = [];
  countries: Country[] = [];

  readonly name = this.fb.control(this.data.name, {
    validators: [Validators.required, Validators.maxLength(255)],
    nonNullable: true,
  });
  readonly addressForm = this.fb.group({
    country_id: this.fb.control<number | null>(
      this.data.address?.country_id ?? null,
    ),
    postcode: this.fb.control(
      this.data.address?.postcode ?? null,
      Validators.maxLength(255),
    ),
    settlement: this.fb.control(
      this.data.address?.settlement ?? null,
      Validators.maxLength(255),
    ),
    street: this.fb.control(
      this.data.address?.street ?? null,
      Validators.maxLength(255),
    ),
  });
  readonly email = this.fb.control(this.data.email ?? null, [
    Validators.email,
    Validators.maxLength(255),
  ]);
  readonly phone = this.fb.control(this.data.phone ?? null, [
    this.helpersService.phoneNumberValidator,
    Validators.maxLength(255),
  ]);
  readonly attorneys = this.fb.control<AttorneyItem[]>(this.data.attorneys, {
    validators: Validators.maxLength(100),
    nonNullable: true,
  });
  readonly form = this.fb.group({
    name: this.name,
    attorneys: this.attorneys,
  });

  loading = false;

  async ngOnInit(): Promise<void> {
    try {
      const { countries } = await this.countriesService.getCountries();
      this.countries = countries;
      const hu = this.countries.find((c) => c.iso === 'HU');
      if (hu && !this.addressForm.value.country_id) {
        this.addressForm.patchValue({ country_id: hu.id });
      }
    } catch (error) {
      this.dialogRef.close();
      console.error('Failed to load countries', error);
      this.snackbar.open('Nem sikerült betölteni az országokat');
      return;
    }

    this.searchAttorneys$
      .pipe(takeUntil(this.destroy), debounceTime(500), distinctUntilChanged())
      .subscribe(async (name) => {
        try {
          this.attorneyOptions = await this.attorneyService.listAttorneys({
            name,
          });
        } catch (error) {
          console.error('Error while getting attorneys', error);
        }
      });

    this.selectedAttorney.valueChanges
      .pipe(
        takeUntil(this.destroy),
        filter((v): v is AttorneyItem => !!v),
      )
      .subscribe((v) => {
        this.attorneys.patchValue(this.attorneys.value.concat(v));
        this.selectedAttorney.patchValue(null);
      });
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }

  removeAttorney(i: number): void {
    const attorneys = [...this.attorneys.value];
    attorneys.splice(i, 1);
    this.attorneys.patchValue(attorneys);
  }

  readonly submit = async () => {
    if (this.form.invalid || this.loading) {
      this.helpersService.markAllChildrenAsDirty(this.form);
      return;
    }

    const name = this.name.getRawValue();
    const email = this.email.getRawValue();
    const phone = this.phone.getRawValue();
    const attorneys = this.attorneys.getRawValue();
    const address = this.lawFirmAddressPayload();

    try {
      this.loading = true;
      await this.lawFirmService.update(this.data.id, {
        attorneys: attorneys.map((a) => a.id),
        address,
        email,
        name,
        phone,
      });
      this.data.name = name;
      this.data.address = address;
      this.data.email = email;
      this.data.phone = phone;
      this.data.attorneys = attorneys;
      this.dialogRef.close(true);
    } catch (error: AxiosError | unknown) {
      console.error('Error while updating law firm', error);
      let message = 'Ismeretlen hiba';
      if (error instanceof AxiosError && error.response?.data.message) {
        message = error.response.data.message;
      }
      this.snackbar.open(message, 'OK', { duration: 5000 });
    } finally {
      this.loading = false;
    }
  };

  readonly cancel = () => {
    this.dialogRef.close(false);
  };

  readonly attorneyDisplayWith = (item?: AttorneyItem): string => {
    return item?.name ?? '';
  };

  private lawFirmAddressPayload(): Address | null {
    const v = this.addressForm.getRawValue();
    if (!v.postcode && !v.settlement && !v.street) {
      return null;
    }

    return {
      country_id: v.country_id,
      postcode: v.postcode,
      settlement: v.settlement,
      street: v.street,
    };
  }
}
